/**
 * SUPPORT-ONLY. OCPP Timelog Viewer.
 *
 * Loads a charger's OCPP 1.6 logs and renders them as an interactive timeline +
 * table (via @chargelab/ocpp-log-visualizer). Beyond a direct charger + window
 * query, support users can resolve the right log slice from a receipt id,
 * transaction id, or session id — which also fills the charger/window fields and
 * (for receipts) overlays the billing breakdown. All state is mirrored to the
 * URL so a link reproduces the exact view. Not exposed to clients.
 */
import '@chargelab/ocpp-log-visualizer/theme.css';
import { OcppLogTableExplorer, OcppTimelineExplorer } from '@chargelab/ocpp-log-visualizer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getReceiptWebAppUrlPrefix } from '../../../stores/selectors/deployment.selector';
import { InlineMessageSeverity } from '../../../stores/types/inlineMessageBanner.interface';
import { Card, Spinner } from '../../_ui';
import { InlineMessage } from '../../_ui/InlineMessage.component';
import {
  decodeTimeSpecString,
  encodeTimeSpecString,
  resolveTimeSpec,
  type TimeSpec,
} from './components/TimeRangePicker.component';
import { LookupPanel } from './components/LookupPanel.component';
import { SessionSummaryCard } from './components/SessionSummaryCard.component';
import { DEFAULT_PADDING_MINUTES, parsePaddingMinutes } from './types';
import { decodeState } from './urlState';
import { useOcppTimelog } from './useOcppTimelog';

const DEFAULT_FROM_SPEC: TimeSpec = {
  mode: 'relative',
  amount: 1,
  unit: 'd',
  dir: 'ago',
  round: false,
};
const DEFAULT_TO_SPEC: TimeSpec = { mode: 'now' };

/** If the user pastes a receipt URL, pull the ID out of the path. */
const extractReceiptId = (input: string): string => {
  const trimmed = input.trim();
  try {
    const url = new URL(trimmed);
    const segments = url.pathname.split('/');
    const idx = segments.indexOf('receipts');
    if (idx !== -1 && segments[idx + 1]) {
      return decodeURIComponent(segments[idx + 1]).trim();
    }
  } catch {
    // Not a URL — use as-is.
  }
  return trimmed;
};

export const OcppTimelogPage = () => {
  const { t } = useTranslation();
  const timelog = useOcppTimelog();
  const webAppUrlPrefix = useSelector(getReceiptWebAppUrlPrefix);

  const [chargerIdInput, setChargerIdInput] = useState('');

  const [fromSpec, setFromSpec] = useState<TimeSpec>(() => {
    const decoded = decodeState(new URLSearchParams(window.location.search));
    if (decoded.fromSpec) {
      const parsed = decodeTimeSpecString(decoded.fromSpec);
      if (parsed) return parsed;
    }
    return DEFAULT_FROM_SPEC;
  });

  const [toSpec, setToSpec] = useState<TimeSpec>(() => {
    const decoded = decodeState(new URLSearchParams(window.location.search));
    if (decoded.toSpec) {
      const parsed = decodeTimeSpecString(decoded.toSpec);
      if (parsed) return parsed;
    }
    return DEFAULT_TO_SPEC;
  });

  const [receiptIdInput, setReceiptIdInput] = useState('');
  const [txnIdInput, setTxnIdInput] = useState('');
  const [sessionIdInput, setSessionIdInput] = useState('');
  const [paddingBeforeInput, setPaddingBeforeInput] = useState(String(DEFAULT_PADDING_MINUTES));
  const [paddingAfterInput, setPaddingAfterInput] = useState(String(DEFAULT_PADDING_MINUTES));

  // Mirror a resolved view back into the input fields (charger id + window),
  // so receipt/transaction/session lookups visibly fill them in.
  const { applied } = timelog;
  useEffect(() => {
    if (!applied) return;
    setChargerIdInput(applied.chargerId);
    // Resolved lookups always produce absolute timestamps — reflect them as
    // absolute TimeSpecs so the picker shows the specific window.
    setFromSpec({ mode: 'absolute', iso: applied.from });
    setToSpec({ mode: 'absolute', iso: applied.to });
    setReceiptIdInput(applied.receiptId ?? '');
    setTxnIdInput(applied.txnId ?? '');
    setSessionIdInput(applied.sessionId ?? '');
  }, [applied]);

  const resolvedPaddingBefore = () => parsePaddingMinutes(paddingBeforeInput);
  const resolvedPaddingAfter = () => parsePaddingMinutes(paddingAfterInput);

  const handleLoadCharger = () => {
    const now = new Date();
    const start = resolveTimeSpec(fromSpec, now);
    const end = resolveTimeSpec(toSpec, now);
    void timelog.loadCharger(
      chargerIdInput,
      start,
      end,
      encodeTimeSpecString(fromSpec),
      encodeTimeSpecString(toSpec),
    );
  };

  const busy = timelog.status === 'resolving' || timelog.status === 'loadingLogs';
  const hasResult = timelog.status === 'ready' && timelog.timeline;

  return (
    <div className='m-6 flex flex-col gap-6'>
      <Card>
        <div className='flex flex-col gap-6 p-6'>
          <h2 className='text-xl font-semibold text-black'>{t('ocpp_timelog.title')}</h2>

          <LookupPanel
            fieldKey={timelog.resetKey}
            busy={busy}
            chargerId={chargerIdInput}
            onChargerIdChange={setChargerIdInput}
            from={fromSpec}
            to={toSpec}
            onTimeRangeChange={(f, t) => {
              setFromSpec(f);
              setToSpec(t);
            }}
            onLoadCharger={handleLoadCharger}
            paddingBefore={paddingBeforeInput}
            onPaddingBeforeChange={setPaddingBeforeInput}
            paddingAfter={paddingAfterInput}
            onPaddingAfterChange={setPaddingAfterInput}
            receiptId={receiptIdInput}
            onReceiptIdChange={(value) => setReceiptIdInput(extractReceiptId(value))}
            onLoadReceipt={() =>
              void timelog.loadReceipt(
                receiptIdInput,
                resolvedPaddingBefore(),
                resolvedPaddingAfter(),
              )
            }
            txnId={txnIdInput}
            onTxnIdChange={setTxnIdInput}
            onLoadTransaction={() =>
              void timelog.loadTransaction(
                txnIdInput,
                resolvedPaddingBefore(),
                resolvedPaddingAfter(),
              )
            }
            sessionId={sessionIdInput}
            onSessionIdChange={setSessionIdInput}
            onLoadSession={() =>
              void timelog.loadSession(
                sessionIdInput,
                resolvedPaddingBefore(),
                resolvedPaddingAfter(),
              )
            }
          />

          {timelog.warning && (
            <InlineMessage
              severity={InlineMessageSeverity.WARNING}
              leadMessage={timelog.warning}
              dataTestId='ocpp-timelog-warning'
            />
          )}

          {timelog.status === 'error' && timelog.error && (
            <InlineMessage
              severity={InlineMessageSeverity.ALERT}
              leadMessage={timelog.error}
              dataTestId='ocpp-timelog-load-error'
            />
          )}

          {busy && (
            <div className='flex justify-center py-12'>
              <Spinner />
            </div>
          )}

          {timelog.status === 'empty' && (
            <div className='flex justify-center py-12 text-grey5'>
              {t('ocpp_timelog.no_messages')}
            </div>
          )}
        </div>
      </Card>

      {hasResult && timelog.receiptSummary && (
        <SessionSummaryCard
          summary={timelog.receiptSummary}
          receiptInfo={timelog.receiptInfo}
          transactionData={timelog.transactionData}
          receiptUrl={
            timelog.receiptInfo?.receiptId
              ? `${webAppUrlPrefix}/receipts/${encodeURIComponent(timelog.receiptInfo.receiptId)}`
              : undefined
          }
        />
      )}

      {hasResult && timelog.timeline && (
        <Card>
          <div className='flex flex-col gap-8 p-6'>
            <OcppTimelineExplorer
              key={`timeline-${timelog.resetKey}`}
              timeline={timelog.timeline}
              extraBands={timelog.extraBands}
              initialFocusWindow={timelog.initialFocusWindow}
              initialViewport={timelog.initialViewport}
              initialScope={timelog.initialScope}
            />
            <OcppLogTableExplorer key={`table-${timelog.resetKey}`} timeline={timelog.timeline} />
          </div>
        </Card>
      )}
    </div>
  );
};
